import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { PRIMARY_NAV } from '../data/site';
import { Logo } from './Logo';
import { ThemeToggle } from './ThemeToggle';

const CTA =
  'sheen inline-flex min-h-11 items-center justify-center rounded-full bg-brand px-5 text-[0.8125rem] ' +
  'font-semibold text-white shadow-[0_10px_28px_-12px_var(--brand-shadow)] transition-transform ' +
  'duration-300 ease-[var(--ease-siv)] hover:-translate-y-0.5 motion-reduce:transition-none';

/** Outlined, so it reads as secondary to "Submit an idea" rather than competing with it. */
const TERMINAL_CTA =
  'border-line-strong bg-surface inline-flex min-h-11 items-center justify-center gap-2 rounded-full ' +
  'border px-4 text-[0.8125rem] font-semibold transition-transform duration-300 ' +
  'ease-[var(--ease-siv)] hover:-translate-y-0.5 motion-reduce:transition-none';

/** A prompt caret in a frame. Inline SVG rather than an icon font or an emoji: currentColor
 *  makes it follow the theme in both light and dark, and it stays crisp at any scale. */
function TerminalIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect x="1.25" y="2.25" width="13.5" height="11.5" rx="2"
            stroke="currentColor" strokeWidth="1.4" />
      <path d="M4.4 6.2 6.3 8.05 4.4 9.9" stroke="currentColor" strokeWidth="1.4"
            strokeLinecap="round" strokeLinejoin="round" />
      <path d="M8.1 10.1h3.4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

/** The way into the product.
 *
 * A plain <a>, NOT a react-router <NavLink>. The terminal is a different application -- the
 * Next.js app served on the same domain -- so handing /sign-in to this SPA's router would
 * match no route and render its own 404 without ever leaving the page. It needs a real
 * navigation, which is also why there is no `onClick={close}`: the document unloads.
 */
function OpenTerminal({ className = '' }: { className?: string }) {
  return (
    <a href="/sign-in" className={`${TERMINAL_CTA} ${className}`} data-testid="nav-terminal">
      <TerminalIcon />
      Open terminal
    </a>
  );
}

/* ------------------------------------------------------------------ */

function DesktopNav() {
  return (
    <nav aria-label="Primary" className="hidden lg:block">
      <ul className="flex items-center gap-1">
        {PRIMARY_NAV.map((item) => (
          <li key={item.to}>
            <NavLink
              to={item.to}
              className={({ isActive }) =>
                `nav-link inline-flex min-h-10 items-center rounded-full px-3.5 text-[0.875rem] font-medium transition-colors duration-300 motion-reduce:transition-none ${
                  isActive ? 'text-text' : 'text-muted hover:text-text'
                }`
              }
            >
              {item.label}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/* ------------------------------------------------------------------ */

export function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const panelId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { pathname } = useLocation();

  const close = useCallback(() => setOpen(false), []);

  // Close on route change so a link tap never leaves the drawer covering the page.
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  // Condense the bar once the page has moved, so it takes less height while reading.
  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      setScrolled(window.scrollY > 12);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  // Focus trap, scroll lock, Escape, and focus restoration for the drawer.
  useEffect(() => {
    if (!open) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    // Captured now: by cleanup time the ref may point at a different node.
    const triggerAtOpen = triggerRef.current;
    const { body } = document;
    const previousOverflow = body.style.overflow;
    body.style.overflow = 'hidden';

    const focusables = () =>
      Array.from(
        panelRef.current?.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      );

    focusables()[0]?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        return;
      }
      if (event.key !== 'Tab') return;

      const items = focusables();
      if (items.length === 0) return;
      const first = items[0]!;
      const last = items[items.length - 1]!;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      body.style.overflow = previousOverflow;
      (previouslyFocused ?? triggerAtOpen)?.focus?.();
    };
  }, [open]);

  return (
    <header
      className={`border-line sticky top-0 z-50 border-b bg-[color-mix(in_oklab,var(--bg)_78%,transparent)] backdrop-blur-xl transition-[padding,box-shadow] duration-300 ease-[var(--ease-siv)] motion-reduce:transition-none ${
        scrolled ? 'py-2 shadow-[0_10px_30px_-24px_rgb(15_30_51/0.6)]' : 'py-3.5'
      }`}
    >
      {/* Made inert while the drawer is open: nothing behind a modal should be
          focusable, hit-testable or announced. */}
      <div
        inert={open}
        className="mx-auto flex w-full max-w-[86rem] items-center gap-4 px-4 sm:px-6 lg:px-8"
      >
        <Logo compact />

        <div className="ml-auto flex items-center gap-2 lg:gap-4">
          <DesktopNav />

          {/* Wrapped rather than given `hidden md:inline-flex` directly: two
              display utilities on one element resolve by stylesheet order,
              which is not something to rely on. */}
          <div className="hidden items-center gap-2.5 md:flex lg:gap-3">
            <ThemeToggle />
            <OpenTerminal />
            <NavLink to="/contact" className={CTA} data-testid="nav-cta">
              Submit an idea
            </NavLink>
          </div>

          <button
            ref={triggerRef}
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls={panelId}
            className="border-line-strong bg-surface inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center gap-2 rounded-full border px-3.5 lg:hidden"
          >
            <span className="flex flex-col gap-[3.5px]" aria-hidden="true">
              <span className="block h-0.5 w-4 rounded-full bg-current" />
              <span className="block h-0.5 w-4 rounded-full bg-current" />
              <span className="block h-0.5 w-4 rounded-full bg-current" />
            </span>
            <span className="font-mono text-[0.6875rem] font-bold tracking-[0.14em] uppercase">
              Menu
            </span>
          </button>
        </div>
      </div>

      {open && (
        <>
          <button
            type="button"
            aria-label="Close menu"
            onClick={close}
            className="bg-navy/50 fixed inset-0 z-40 backdrop-blur-sm lg:hidden"
          />
          <div
            ref={panelRef}
            id={panelId}
            role="dialog"
            aria-modal="true"
            aria-label="Site menu"
            className="animate-page-in border-line bg-bg-2 fixed inset-x-0 top-0 z-50 max-h-[100dvh] overflow-y-auto border-b px-4 pt-4 pb-8 shadow-2xl sm:px-6 lg:hidden"
          >
            <div className="mb-7 flex items-center justify-between gap-4">
              <Logo compact />
              <button
                type="button"
                onClick={close}
                className="border-line-strong inline-flex min-h-11 min-w-11 items-center justify-center rounded-full border px-4 font-mono text-[0.6875rem] font-bold tracking-[0.14em] uppercase"
              >
                Close
              </button>
            </div>

            {/* Large, generously spaced targets — this is the primary way the
                site is navigated on a phone. */}
            <nav aria-label="Primary">
              <ul className="flex flex-col">
                {PRIMARY_NAV.map((item) => (
                  <li key={item.to} className="border-line border-b last:border-b-0">
                    <NavLink
                      to={item.to}
                      onClick={close}
                      className={({ isActive }) =>
                        `flex min-h-16 items-center gap-4 text-[1.25rem] font-semibold transition-colors duration-200 motion-reduce:transition-none ${
                          isActive ? 'text-blue' : 'text-text'
                        }`
                      }
                    >
                      {item.label}
                      <span aria-hidden="true" className="text-faint ml-auto text-lg">
                        →
                      </span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            </nav>

            <OpenTerminal className="mt-7 w-full" />
            <NavLink to="/contact" onClick={close} className={`${CTA} mt-3 w-full`}>
              Submit an idea
            </NavLink>

            <div className="border-line mt-7 flex items-center justify-between gap-4 border-t pt-6 md:hidden">
              <span className="font-mono text-faint text-[0.6875rem] font-bold tracking-[0.14em] uppercase">
                Appearance
              </span>
              <ThemeToggle />
            </div>
          </div>
        </>
      )}
    </header>
  );
}
